// src/components/Layout.js
import React, { useState, useEffect } from "react"; // Added useEffect
import { supabase } from "../utils/supabaseClient";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  useMediaQuery,
  useTheme,
  Button,
  Menu,
  MenuItem,
  Divider,
  ListItemText,
  CircularProgress,
  Select,
  Stack,
  Grid,
  Tooltip,
  Link as MuiLink,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Outlet, Link, useNavigate } from "react-router-dom";
import LoginModal from "./Users/Login";
import SignUpModal from "./Users/Signup";
import SubscriptionLock from "./Users/SubscriptionLock";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { logger } from "../utils/logger";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Chip } from "@mui/material";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { alpha } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";

const Layout = ({ setThemeMode, userCurrency, setUserCurrency }) => {
  // --- Local State for Auth (Working Version) ---
  const [user, setUser] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [paymentLockOpen, setPaymentLockOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // --- Existing Layout State ---
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [openSignUp, setOpenSignUp] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    const handler = () => {
      if (window.gtag) window.gtag("event", "signup_modal_opened");
      setOpenSignUp(true);
    };
    window.addEventListener("open-signup", handler);
    return () => window.removeEventListener("open-signup", handler);
  }, []);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currencyOptions = [
    { code: "AUD", label: "AUD (A$)", name: "Australian Dollar" },
    { code: "BRL", label: "BRL (R$)", name: "Brazilian Real" },
    { code: "CAD", label: "CAD (C$)", name: "Canadian Dollar" },
    { code: "CHF", label: "CHF (Fr)", name: "Swiss Franc" },
    { code: "CNY", label: "CNY (¥)", name: "Chinese Yuan" },
    { code: "DKK", label: "DKK (kr)", name: "Danish Krone" },
    { code: "EUR", label: "EUR (€)", name: "Euro" },
    { code: "GBP", label: "GBP (£)", name: "British Pound" },
    { code: "HKD", label: "HKD (HK$)", name: "Hong Kong Dollar" },
    { code: "INR", label: "INR (₹)", name: "Indian Rupee" },
    { code: "JPY", label: "JPY (¥)", name: "Japanese Yen" },
    { code: "KRW", label: "KRW (₩)", name: "South Korean Won" },
    { code: "MXN", label: "MXN (Mex$)", name: "Mexican Peso" },
    { code: "NOK", label: "NOK (kr)", name: "Norwegian Krone" },
    { code: "NZD", label: "NZD (NZ$)", name: "New Zealand Dollar" },
    { code: "SEK", label: "SEK (kr)", name: "Swedish Krona" },
    { code: "SGD", label: "SGD (S$)", name: "Singapore Dollar" },
    { code: "USD", label: "USD ($)", name: "US Dollar" },
  ];

  useEffect(() => {
    const handleVisibilityChange = async () => {
      // When the document becomes visible (i.e., user returns to the tab)
      if (document.visibilityState === "visible") {
        logger.log("🔄 Tab is visible again. Checking session status...");
        const {
          data: { session },
        } = await supabase.auth.getSession();

        // If the session is no longer valid, you can prompt the user or trigger a refresh.
        if (!session) {
          logger.warn("⚠️ User session appears expired or is missing.");
          // Optionally, you can log the user out or force a page reload.
          // await supabase.auth.signOut();
          // window.location.reload();
        } else {
          logger.log("✅ Session is still active.");
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // --- useEffect for Auth Handling (Working Auth Logic) ---
  // --- useEffect for Auth Handling ---
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    // 🛑 SAFETY TIMER: Force the site to load if Supabase takes too long (>2s)
    const safetyTimer = setTimeout(() => {
      if (isMounted && loading) {
        console.warn("⚠️ Auth check took too long. Forcing load.");
        setLoading(false); // Kill the spinner
      }
    }, 2000);

    // Function to fetch profile
    const fetchUserProfile = async (authUser) => {
      if (!authUser) return null;
      try {
        const { data: profile, error } = await supabase
          .from("user_profiles")
          .select("name, country, has_paid")
          .eq("id", authUser.id)
          .single();

        if (error && error.code !== "PGRST116") return null;

        if (profile) {
          const hasPaid = profile.has_paid === true;
          // 👇 THIS SETS THE VARIABLE 👇
          if (!hasPaid) {
            setPaymentLockOpen(true);
            if (window.gtag) window.gtag("event", "paywall_hit");
          } else {
            setPaymentLockOpen(false);
          }
          // Cache the data
          const profileData = {
            name: profile.name,
            country: profile.country,
            has_paid: hasPaid,
          };
          localStorage.setItem(
            "flybnb_user_cache",
            JSON.stringify(profileData)
          );
          return profileData;
        }
      } catch (e) {
        console.error(e);
      }
      return null;
    };

    const initializeAuth = async () => {
      // 1. FAST CHECK: Load Cache Immediately
      const cached = localStorage.getItem("flybnb_user_cache");

      try {
        // 2. Get Session (This is what was hanging!)
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        const initialUser = session?.user ?? null;

        // 3. Apply Cache if matches (Instant Load)
        if (initialUser && cached) {
          const parsed = JSON.parse(cached);
          if (isMounted) {
            setUser({ ...initialUser, ...parsed });
            setIsPro(!!parsed.has_paid);
            setLoading(false); // Stop spinner
            clearTimeout(safetyTimer); // Cancel safety timer
          }
        } else if (isMounted) {
          // No cache or no user? Show UI immediately.
          setUser(initialUser);
          setLoading(false);
          clearTimeout(safetyTimer);
        }

        // 4. Background Verify (Updates data silently)
        if (initialUser) {
          fetchUserProfile(initialUser).then((profile) => {
            if (isMounted && profile) {
              setUser({ ...initialUser, ...profile });
              setIsPro(profile.has_paid);
            }
          });
        }
      } catch (err) {
        console.error("Auth Init Error:", err);
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    // Listener for Login/Logout
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_OUT") {
          localStorage.removeItem("flybnb_user_cache");
          if (isMounted) {
            setUser(null);
            setIsPro(false);
            // Don't set loading true here, just clear data
          }
        } else if (event === "SIGNED_IN" && session?.user) {
          // Check local storage immediately to preserve the name while DB fetches
          const cached = localStorage.getItem("flybnb_user_cache");
          const parsedCache = cached ? JSON.parse(cached) : null;

          if (isMounted) {
            setUser((prev) => ({
              ...session.user,
              // PRIORITY: Cache -> Previous State -> Metadata -> Fallback
              name:
                parsedCache?.name || // 1. Use the cached name from Stripe/DB
                prev?.name || // 2. Use current state name if valid
                session.user.user_metadata?.full_name ||
                session.user.user_metadata?.name,
              // Merge other profile props immediately to prevent flickering
              ...parsedCache,
            }));

            // Set Pro status immediately if we have it in cache
            if (parsedCache?.has_paid) {
              setIsPro(true);
            }
          }

          // Google OAuth new-user flow: if a pending plan was saved before OAuth redirect,
          // and the user hasn't already paid, skip the modal and go straight to Stripe.
          const pendingPlan = localStorage.getItem("flybnb_pending_plan");
          if (pendingPlan && !parsedCache?.has_paid) {
            localStorage.removeItem("flybnb_pending_plan");
            const themeMode = localStorage.getItem("themeMode") || "dark";
            try {
              const { data: fnData, error: fnError } = await supabase.functions.invoke(
                "create-stripe-checkout",
                { body: JSON.stringify({ email: session.user.email, plan: pendingPlan, theme: themeMode }) }
              );
              if (!fnError && fnData?.checkout_url) {
                window.location.href = fnData.checkout_url;
                return;
              }
            } catch (e) {
              console.error("[GoogleSignup] Checkout redirect failed:", e);
              // Fall through — SubscriptionLock modal will catch them
            }
          }

          fetchUserProfile(session.user).then((p) => {
            if (isMounted && p) {
              setUser({ ...session.user, ...p });
              setIsPro(p.has_paid);
            }
          });
        }
      }
    );

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // --- Component Handlers ---
  const switchToSignUp = () => {
    setOpenLogin(false);
    setOpenSignUp(true);
  };

  const switchToLogin = () => {
    setOpenSignUp(false);
    setOpenLogin(true);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const selectedCurrency =
    currencyOptions.find((c) => c.code === userCurrency)?.label || userCurrency;

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    handleMenuClose();

    try {
      const { data, error } = await supabase.functions.invoke(
        "create-customer-portal"
      );

      if (error) {
        throw new Error(error.message || "Failed to create portal session.");
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Portal URL not received.");
      }
    } catch (error) {
      logger.error("Error creating Stripe Customer Portal session:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleLogout = async () => {
    handleMenuClose();
    setError("");

    // 0) UI immediately
    setUser(null);
    setIsPro(false);

    // 1) Clear your own cache
    localStorage.removeItem("flybnb_user_cache");

    // 2) 🔥 Force-clear Supabase auth token storage (this is the key)
    try {
      const key = supabase.auth.storageKey; // e.g. "sb-xxxxx-auth-token"
      localStorage.removeItem(key);
      sessionStorage.removeItem(key); // harmless if not used
    } catch (e) {
      console.warn("Could not clear supabase storageKey", e);
    }

    // 3) Attempt signOut but don't let it hang forever
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: "local" }), // local is enough for browser SPA
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("signOut_timeout")), 1500)
        ),
      ]);
    } catch (err) {
      console.warn("signOut did not complete cleanly:", err);
    }

    // 4) Hard reload to reset app state
    window.location.assign("/");
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // --- Navbar & Header UI (restored from your original UI) ---
  const navLinks = (
    <Box
      sx={{
        display: "flex",
        gap: isMobile ? 1 : 2.5,
        alignItems: "center",
        justifyContent: "flex-end",
        px: 1,
      }}
    >
      {/* Currency Selector */}
      <Select
        value={userCurrency}
        onChange={(e) => {
          const selected = e.target.value;
          setUserCurrency(selected);
          localStorage.setItem(
            "userCurrency",
            JSON.stringify({ code: selected, date: new Date().toISOString() })
          );
        }}
        renderValue={() => selectedCurrency}
        size="small"
        variant="standard"
        disableUnderline
        sx={{
          fontWeight: 500,
          fontSize: "0.9rem",
          color: theme.palette.mode === "dark" ? alpha("#fff", 0.55) : alpha("#000", 0.45),
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            py: 0,
            pr: "24px !important",
            "&:focus": { background: "none" },
          },
          "& .MuiSelect-icon": {
            color: theme.palette.mode === "dark" ? alpha("#fff", 0.4) : alpha("#000", 0.35),
          },
          "&:hover": {
            color: theme.palette.text.primary,
            "& .MuiSelect-icon": { color: theme.palette.text.primary },
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              px: 2,
              py: 1,
              width: 480,
              borderRadius: 2,
            },
          },
          MenuListProps: {
            sx: {
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
            },
          },
        }}
      >
        {currencyOptions.map((option) => (
          <MenuItem
            key={option.code}
            value={option.code}
            sx={{
              flexDirection: "column",
              alignItems: "flex-start",
              py: 1,
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              {option.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.name}
            </Typography>
          </MenuItem>
        ))}
      </Select>

      {/* Theme Toggle */}
      <Tooltip title="Toggle Light/Dark Mode" arrow>
        <IconButton
          onClick={() => {
            const next = theme.palette.mode === "dark" ? "light" : "dark";
            localStorage.setItem("themeMode", next);
            setThemeMode(next);
          }}
          sx={{
            color: theme.palette.mode === "dark" ? alpha("#fff", 0.45) : alpha("#000", 0.35),
            fontSize: 18,
            p: 0.75,
            "&:hover": {
              background: "none",
              color: theme.palette.text.primary,
            },
          }}
        >
          {theme.palette.mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Navigation Links */}
      <Button
        component={Link}
        to="/about"
        sx={{
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9rem",
          color: theme.palette.mode === "dark" ? alpha("#fff", 0.55) : alpha("#000", 0.45),
          background: "none",
          boxShadow: "none",
          minWidth: 0,
          px: 1,
          "&:hover": {
            background: "none",
            color: theme.palette.text.primary,
          },
        }}
      >
        About
      </Button>
      <Button
        component={Link}
        to="/contact"
        sx={{
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.9rem",
          color: theme.palette.mode === "dark" ? alpha("#fff", 0.55) : alpha("#000", 0.45),
          background: "none",
          boxShadow: "none",
          minWidth: 0,
          px: 1,
          "&:hover": {
            background: "none",
            color: theme.palette.text.primary,
          },
        }}
      >
        Contact
      </Button>

      {/* Auth Actions */}
      {!user ? (
        <Stack direction="row" spacing={1.25} alignItems="center">
          {/* Login (stands out more + square-ish) */}
          <Button
            onClick={() => setOpenLogin(true)}
            startIcon={<PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 40,
              px: 2.4,
              borderRadius: "12px", // ✅ square-ish like your UI
              fontWeight: 800,
              fontSize: "0.9rem",
              textTransform: "none",

              // more visible than plain outline
              color:
                theme.palette.mode === "dark"
                  ? "#fff"
                  : theme.palette.text.primary,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.18)
                  : alpha(theme.palette.primary.main, 0.1),

              border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.45 : 0.35)}`,
              boxShadow: "none",
              transition:
                "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",

              "&:hover": {
                transform: "translateY(-1px)",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.main, 0.26)
                    : alpha(theme.palette.primary.main, 0.14),
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 10px 20px rgba(0,0,0,0.35)"
                    : "0 10px 18px rgba(0,0,0,0.12)",
              },
              "&:active": { transform: "translateY(0px)" },
            }}
          >
            Login
          </Button>

          {/* Pro (premium gradient + square-ish) */}
          <Button
            onClick={() => setOpenSignUp(true)}
            startIcon={<FlightTakeoffRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 40,
              px: 2.8,
              borderRadius: "12px",
              fontWeight: 900,
              fontSize: "0.9rem",
              textTransform: "none",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              background: `linear-gradient(135deg,
      ${theme.palette.primary.main},
      ${theme.palette.primary.stay || theme.palette.primary.dark}
    )`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 14px 30px rgba(95, 90, 162, 0.35)"
                  : "0 14px 26px rgba(95, 90, 162, 0.25)",
              transition:
                "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
              "&:hover": {
                transform: "translateY(-2px)",
                filter: "brightness(1.07)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 18px 44px rgba(95, 90, 162, 0.45)"
                    : "0 18px 40px rgba(95, 90, 162, 0.32)",
              },
            }}
          >
            Sign Up
          </Button>
        </Stack>
      ) : (
        <>
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: 1,
              px: 1.25,
              py: 0.7,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.03)",
              transition: "all 180ms ease",
              "&:hover": {
                transform: "translateY(-1px)",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.22)"
                    : "rgba(0,0,0,0.18)",
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            <AccountCircleIcon
              sx={{
                width: 30,
                height: 30,
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.78)"
                    : "#333",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.01em",
                lineHeight: 1,
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.name ||
                user.user_metadata?.full_name ||
                user.user_metadata?.name ||
                user.email.split("@")[0]}
            </Typography>
          </Box>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 250,
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 18px 60px rgba(0,0,0,0.55)"
                      : "0 18px 60px rgba(0,0,0,0.18)",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(20,20,24,0.92)"
                      : "rgba(255,255,255,0.92)",
                  backdropFilter: "saturate(180%) blur(12px)",
                  WebkitBackdropFilter: "saturate(180%) blur(12px)",
                  py: 0.75,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              disabled
              sx={{
                opacity: 1,
                cursor: "default",
                py: 1.1,
                px: 1.25,
                // ✅ override disabled styling color bleed
                "&.Mui-disabled": { opacity: 1 },
              }}
            >
              <EmailOutlinedIcon
                sx={{
                  mr: 1.1,
                  color: theme.palette.text.secondary, // readable in both modes
                  opacity: 0.9,
                }}
              />

              <ListItemText
                primary={
                  user.name ||
                  user.user_metadata?.full_name ||
                  user.user_metadata?.name ||
                  user.email.split("@")[0]
                }
                secondary={user.email}
                primaryTypographyProps={{
                  variant: "body2",
                  sx: {
                    fontWeight: 900,
                    letterSpacing: "-0.01em",
                    color: theme.palette.text.primary, // ✅ force readable
                    opacity: 1,
                  },
                }}
                secondaryTypographyProps={{
                  variant: "caption",
                  sx: {
                    color: theme.palette.text.secondary, // ✅ readable but softer
                    opacity: 0.95,
                  },
                }}
              />

              {isPro && (
                <Box
                  sx={{
                    ml: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: theme.palette.primary.main,
                    color: "#fff",
                    fontSize: "0.75rem",
                    fontWeight: 900,
                  }}
                >
                  <StarRoundedIcon sx={{ fontSize: 16 }} />
                  PRO
                </Box>
              )}
            </MenuItem>

            <Divider sx={{ my: 0.75, opacity: 0.7 }} />

            {isPro && (
              <MenuItem
                onClick={handleManageSubscription}
                disabled={portalLoading}
                sx={{
                  py: 1.05,
                  px: 1.25,
                  borderRadius: 2,
                  mx: 0.75,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <ManageAccountsOutlinedIcon sx={{ mr: 1.1 }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {portalLoading ? "Loading..." : "Manage Subscription"}
                </Typography>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                handleMenuClose();
                window.dispatchEvent(new CustomEvent("open-saved-searches"));
              }}
              sx={{
                py: 1.05,
                px: 1.25,
                borderRadius: 2,
                mx: 0.75,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <BookmarkBorderOutlinedIcon sx={{ mr: 1.1 }} />
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Saved Searches
              </Typography>
              {!isPro && (
                <Box
                  sx={{
                    ml: "auto",
                    px: 0.9,
                    py: 0.2,
                    borderRadius: 999,
                    bgcolor: theme.palette.primary.main,
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 900,
                    letterSpacing: "0.05em",
                  }}
                >
                  PRO
                </Box>
              )}
            </MenuItem>

            {/* Only show "Reset Password" for users who actually have a
                password (email/password signups). OAuth-only users (e.g. Google)
                have no password to reset — clicking it would just add one as a
                second login method, which is confusing UX. */}
            {(user.app_metadata?.provider === "email" ||
              (user.identities || []).some((i) => i.provider === "email")) && (
              <MenuItem
                component={Link}
                to="/reset-password"
                onClick={handleMenuClose}
                sx={{
                  py: 1.05,
                  px: 1.25,
                  borderRadius: 2,
                  mx: 0.75,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <LockOutlinedIcon sx={{ mr: 1.1 }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Reset Password
                </Typography>
              </MenuItem>
            )}

            <Divider sx={{ my: 0.75, opacity: 0.7 }} />

            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.05,
                px: 1.25,
                borderRadius: 2,
                mx: 0.75,
                color: "error.main",
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(244,67,54,0.15)"
                      : "rgba(244,67,54,0.08)",
                },
              }}
            >
              <LogoutOutlinedIcon sx={{ mr: 1.1 }} />
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Modals */}
      <LoginModal
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        switchToSignUp={switchToSignUp}
      />
      <SignUpModal
        open={openSignUp}
        onClose={() => setOpenSignUp(false)}
        switchToLogin={switchToLogin}
      />
      {/* 👇 NEW: Google and Email Signup / Payment Lock Modal 👇 */}
      {user && (
        <SubscriptionLock
          open={paymentLockOpen}
          user={user}
          onLogout={handleLogout}
        />
      )}
    </Box>
  );

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      PaperProps={{
        sx: {
          width: 260, // thinner drawer
          // Glass (frosted) background
          bgcolor:
            theme.palette.mode === "light"
              ? "rgba(255,255,255,0.72)"
              : "rgba(10,10,12,0.72)",
          color: "text.primary",
          backdropFilter: "saturate(180%) blur(14px)",
          WebkitBackdropFilter: "saturate(180%) blur(14px)",
          borderTopLeftRadius: 14,
          borderBottomLeftRadius: 14,
          boxShadow:
            theme.palette.mode === "light"
              ? "0 10px 30px rgba(0,0,0,0.12)"
              : "0 14px 36px rgba(0,0,0,0.45)",
          borderLeft: `1px solid ${theme.palette.divider}`,
          transition: "transform 350ms cubic-bezier(.2,.8,.2,1)",
        },
      }}
      ModalProps={{ keepMounted: true }}
      SlideProps={{ timeout: { enter: 280, exit: 220 } }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        {/* Header row with logo + close (no text title) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <img
            src={
              theme.palette.mode === "dark"
                ? "/logo/dark-main-logo.svg"
                : "/logo/light-main-logo.svg"
            }
            alt="FlyBnB"
            style={{ height: 28 }}
          />
          <IconButton
            size="small"
            onClick={toggleDrawer(false)}
            sx={{
              color: "text.secondary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Mini profile card (when logged in) */}
        {user && (
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              gap: 1,
              px: 1.25,
              py: 0.7,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.03)",
              color: "text.primary", // ✅ makes children inherit correct color
              transition: "all 180ms ease",
              "&:hover": {
                /* ... */
              },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1,
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "text.primary", // ✅ this is the key (not "#fff")
                }}
              >
                {user.name ||
                  user.user_metadata?.full_name ||
                  user.user_metadata?.name ||
                  user.email.split("@")[0]}
              </Typography>
              <Typography variant="caption" noWrap color="text.secondary">
                {user.email}
              </Typography>
            </Box>
            {isPro && (
              <Chip
                size="small"
                icon={<StarRoundedIcon sx={{ fontSize: 16 }} />}
                label="Pro"
                sx={{
                  ml: "auto",
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: theme.palette.primary.main,
                  "& .MuiChip-icon": { color: "#fff !important" },
                }}
              />
            )}
          </Box>
        )}

        {/* Quick actions: vertical stack (Currency + Theme) */}
        {/* Quick actions: vertical stack (Currency Select + Theme) */}
        <Stack spacing={0.75} sx={{ mb: 1.25 }}>
          {/* Currency dropdown (single column) */}
          <Select
            value={userCurrency}
            onChange={(e) => {
              const selected = e.target.value;
              setUserCurrency(selected);
              localStorage.setItem(
                "userCurrency",
                JSON.stringify({
                  code: selected,
                  date: new Date().toISOString(),
                })
              );
            }}
            renderValue={() => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CurrencyExchangeOutlinedIcon fontSize="small" />
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  {selectedCurrency}
                </Typography>
              </Box>
            )}
            size="small"
            fullWidth
            sx={{
              fontWeight: 600,
              fontSize: "0.95rem",
              borderRadius: 2,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              color: "text.primary",
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                py: 1,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  px: 1.5,
                  py: 1,
                  width: 248, // fits inside 260px drawer
                  borderRadius: 2,
                },
              },
              MenuListProps: {
                sx: {
                  display: "grid",
                  gridTemplateColumns: "1fr", // single column
                  gap: 0.5,
                },
              },
            }}
          >
            {currencyOptions.map((option) => (
              <MenuItem
                key={option.code}
                value={option.code}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  py: 1,
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.name}
                </Typography>
              </MenuItem>
            ))}
          </Select>

          {/* Theme toggle (unchanged) */}
          <Button
            variant="outlined"
            startIcon={
              theme.palette.mode === "dark" ? (
                <Brightness7Icon fontSize="small" />
              ) : (
                <Brightness4Icon fontSize="small" />
              )
            }
            onClick={() => {
              const next = theme.palette.mode === "dark" ? "light" : "dark";
              localStorage.setItem("themeMode", next);
              setThemeMode(next);
            }}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontSize: "0.9rem",
              borderRadius: 2,
              borderColor: "divider",
              color: "text.primary",
              py: 0.75,
              minHeight: 40,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {theme.palette.mode === "dark" ? "Light mode" : "Dark mode"}
          </Button>
        </Stack>
      </Box>

      <Divider />

      <List sx={{ px: 1, py: 0.5 }}>
        {!user && (
          <ListItem disableGutters sx={{ px: 1, py: 0.9 }}>
            <Button
              fullWidth
              startIcon={<LoginIcon />}
              onClick={() => {
                setOpenLogin(true);
                setDrawerOpen(false);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                py: 0.9,
                fontSize: "0.95rem",
                boxShadow: "0 8px 20px rgba(95, 90, 162, 0.35)",
                background: `linear-gradient(45deg, ${theme.palette.primary.stay}, ${theme.palette.primary.main})`,
                color: "#fff",
                transition: "transform 180ms ease, box-shadow 180ms ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 12px 24px rgba(95, 90, 162, 0.45)",
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.stay})`,
                },
              }}
            >
              Login / Sign Up
            </Button>
          </ListItem>
        )}
        <ListItem disableGutters sx={{ px: 1 }}>
          <Button
            fullWidth
            component={Link}
            to="/"
            startIcon={<TravelExploreOutlinedIcon />}
            onClick={() => {
              setDrawerOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" }); // 👈 scroll to top
            }}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              py: 0.85,
              fontSize: "0.95rem",
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            Search Trips
          </Button>
        </ListItem>
        <ListItem disableGutters sx={{ px: 1 }}>
          <Button
            fullWidth
            component={Link}
            to="/about"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setDrawerOpen(false)} // ✅ close drawer on navigation
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              py: 0.85,
              fontSize: "0.95rem",
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            About
          </Button>
        </ListItem>

        <ListItem disableGutters sx={{ px: 1 }}>
          <Button
            fullWidth
            component={Link}
            to="/contact"
            startIcon={<ContactSupportOutlinedIcon />}
            onClick={() => setDrawerOpen(false)} // ✅ close drawer on navigation
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              py: 0.85,
              fontSize: "0.95rem",
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            Contact
          </Button>
        </ListItem>

        {user && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem disableGutters sx={{ px: 1 }}>
              <Button
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={() => {
                  handleLogout();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  py: 0.85,
                  fontSize: "0.95rem",
                  color: "error.main",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                Logout
              </Button>
            </ListItem>
          </>
        )}
      </List>

      {/* Drawer footer */}
      <Box sx={{ mt: "auto", px: 1.5, pb: 1.5 }}>
        <Divider sx={{ mb: 1 }} />
        <MuiLink
          href="mailto:info@flybnb.io"
          variant="caption"
          color="text.secondary"
          sx={{ "&:hover": { textDecoration: "underline" } }}
        >
          info@flybnb.io
        </MuiLink>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.25 }}
        >
          © {currentYear} FlyBnB — Made in Canada ❤️
        </Typography>
      </Box>
    </Drawer>
  );

  // --- Main Return Structure ---
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: theme.custom.navbar,
          boxShadow: "none",
          borderBottom: theme.palette.mode === "light" ? "none" : `1.5px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary, // ✅ fixes inherited white text
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexGrow: 1,
                mt: { xs: 0.5, md: 2 },
                mb: { xs: 0.5, md: 2 },
              }}
            >
              <img
                src={theme.custom.logo}
                alt="FlyBnB Logo"
                title="FlyBnB Logo"
                style={{
                  height: isMobile ? "35px" : "50px",
                  margin: isMobile ? 4 : 8,
                  cursor: "pointer",
                }}
                onClick={() => (window.location.href = "/")}
              />
            </Box>
            {isMobile ? (
              <>
                <IconButton
                  edge="start"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    ml: 0,
                    mr: "auto",
                    color: theme.palette.mode === "light" ? "#222" : "#f1f1f1", // 👈 force dark in light mode
                  }}
                >
                  <MenuIcon />
                </IconButton>
                {mobileMenu}
              </>
            ) : (
              navLinks
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Box component="main" sx={{ flex: 1, p: 0, mt: 0, pb: 6 }}>
        {error && (
          <Container maxWidth="lg" sx={{ mb: 2 }}>
            <Typography color="error" align="center" variant="body2">
              {error}
            </Typography>
          </Container>
        )}
        <Outlet
          context={{
            userCurrency,
            setUserCurrency,
            onSignUpClick: () => setOpenSignUp(true),
            isPro,
          }}
        />{" "}
      </Box>

      {/* Footer Section - Improved Column Layout */}
      <Box
        component="footer"
        sx={{
          backgroundColor:
            theme.palette.mode === "dark" ? "#1A1A1A" : "#f8f9fa", // Use theme colors
          py: { xs: 3, md: 5 }, // Vertical padding
          mt: "auto", // Pushes footer to bottom
          borderTop: `1px solid ${theme.palette.divider}`, // Use theme divider color
          color: "text.secondary", // Use theme text color
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="space-between">
            {/* Column 1: Company Info & Contact */}
            <Grid item xs={12} sm={6} md={4}>
              {" "}
              {/* Logo Placement */}
              <Box sx={{ mb: 1.5, textAlign: "left" }}>
                <img
                  // Conditionally set the source based on the theme mode
                  src={
                    theme.palette.mode === "dark"
                      ? "/logo/dark-main-logo.svg" // <-- Replace with DARK mode logo path
                      : "/logo/light-main-logo.svg" // <-- Replace with LIGHT mode logo path
                  }
                  alt="FlyBnB Logo"
                  style={{
                    height: isMobile ? "30px" : "40px", // Adjust height as needed
                    width: "auto", // Maintain aspect ratio
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                />
              </Box>
              {/* End Logo Placement */}
              <Typography
                variant="caption"
                display="block"
                sx={{ mb: 1, textAlign: "left" }}
              >
                Search flights and Airbnb stays together — and see what your
                trip actually costs. Built by two travelers who got tired of
                spreadsheets.
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ mb: 0.5, textAlign: "left" }}
              >
                <MuiLink
                  href="mailto:info@flybnb.io"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  info@flybnb.io
                </MuiLink>
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{ textAlign: "left" }}
              >
                Made in Canada with ❤️
              </Typography>
              {/* Disclaimer integrated here */}
              {isPro && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "success.main",
                    mt: 1,
                    textAlign: "left",
                  }}
                >
                  Thank you for being a Pro member! ✨
                </Typography>
              )}
            </Grid>

            {/* Column 2: Quick Links */}
            <Grid item xs={6} sm={3} md={2}>
              {" "}
              {/* Standard column width */}
              <Typography
                variant="overline"
                sx={{
                  fontWeight: "bold",
                  display: "block",
                  mb: 1,
                  textAlign: "left",
                }}
              >
                Links
              </Typography>
              <Stack spacing={0.5} alignItems="flex-start">
                <MuiLink
                  component={Link}
                  to="/"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  Home
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/about"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  About
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/how-it-works"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  How It Works
                </MuiLink>

                <MuiLink
                  component={Link}
                  to="/contact"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  Contact
                </MuiLink>
              </Stack>
            </Grid>

            {/* Column 3: Resources */}
            <Grid item xs={6} sm={3} md={2}>
              {" "}
              {/* Standard column width */}
              <Typography
                variant="overline"
                sx={{
                  fontWeight: "bold",
                  display: "block",
                  mb: 1,
                  textAlign: "left",
                }}
              >
                Resources
              </Typography>
              <Stack spacing={0.5} alignItems="flex-start">
                <MuiLink
                  component={Link}
                  to="/blog"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  Blog
                </MuiLink>
                {/* Add more resource links if applicable */}
                {/* e.g., <MuiLink href="https://skyscanner.com" target="_blank" rel="noopener noreferrer" variant="body2" color="text.secondary" sx={{ '&:hover': { textDecoration: 'underline' }}}>Skyscanner</MuiLink> */}
              </Stack>
            </Grid>

            {/* Column 4: Legal */}
            <Grid item xs={6} sm={3} md={2}>
              {" "}
              {/* Standard column width */}
              <Typography
                variant="overline"
                sx={{
                  fontWeight: "bold",
                  display: "block",
                  mb: 1,
                  textAlign: "left",
                }}
              >
                Legal
              </Typography>
              <Stack spacing={0.5} alignItems="flex-start">
                {/* Ensure these paths match your router setup */}
                <MuiLink
                  component={Link}
                  to="/termsofservice"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  Terms of Service
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/privacy"
                  variant="body2"
                  color="text.secondary"
                  sx={{ "&:hover": { textDecoration: "underline" } }}
                >
                  Privacy Policy
                </MuiLink>
              </Stack>
            </Grid>

            {/* Add a Social Links column here if desired, adjust md widths accordingly */}
          </Grid>

          {/* Bottom Bar with Copyright */}
          <Box
            sx={{
              textAlign: "center",
              pt: 4,
              mt: 4,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              © {currentYear} Flybnb.io. All Rights Reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
      <LoginModal
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        switchToSignUp={switchToSignUp}
      />
      <SignUpModal
        open={openSignUp}
        onClose={() => setOpenSignUp(false)}
        switchToLogin={switchToLogin}
      />
    </Box>
  );
};

export default Layout;
